import React, { useState, useEffect, useRef } from "react";
import styles from "./preset-budget-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Dropdown from "../../ui/dropdown";
import Button from "../../ui/button";
import Table from "../../ui/table";
import FormSection from "../../screens/form-section";
import { getDefaultBudgets, getGuilds, createPredeterminedBudget, getPredeterminedBudgetById, editPredeterminedBudget, deletePredeterminedBudget, searchPredeterminedBudgetsByName } from "../../../services/apiService";
import { showErrorToast } from "../../../utils/notifications";

const PresetBudgetModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    guilds: [], // Cambiado a array para multiselect
    description: "",
  });
  const [presetBudgets, setPresetBudgets] = useState([]);
  const [guildOptions, setGuildOptions] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingBudgetId, setEditingBudgetId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch preset budgets
        const budgetsData = await getDefaultBudgets();
        setPresetBudgets(budgetsData);

        // Fetch guilds
        const guildsData = await getGuilds();
        const formattedGuilds = guildsData.map(guild => ({
          label: guild.nombre,
          value: guild.id
        }));
        setGuildOptions(formattedGuilds);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownSelect = (selectedGuilds) => {
    setFormData({ ...formData, guilds: selectedGuilds });
  };

  const handleAddPresetBudget = async () => {
    try {
      // Validaciones
      if (!formData.name) {
        showErrorToast("El nombre es obligatorio");
        return;
      }
      if (formData.guilds.length === 0) {
        showErrorToast("Debe seleccionar al menos un gremio");
        return;
      }

      const budgetData = {
        nombre: formData.name,
        gremiosIds: formData.guilds.map(guild => guild.value),
        descripcion: formData.description
      };

      await createPredeterminedBudget(budgetData);
      
      // Refrescar la lista de presupuestos
      const budgetsData = await getDefaultBudgets();
      setPresetBudgets(budgetsData);
      
      // Limpiar el formulario
      setFormData({
        name: "",
        guilds: [],
        description: "",
      });
      
    } catch (error) {
      console.error("Error al crear presupuesto predeterminado:", error);
    }
  };

  const handleEditPresetBudget = async () => {
    try {
      // Validaciones
      if (!formData.name) {
        showErrorToast("El nombre es obligatorio");
        return;
      }
      if (formData.guilds.length === 0) {
        showErrorToast("Debe seleccionar al menos un gremio");
        return;
      }

      const budgetData = {
        nombre: formData.name,
        gremiosIds: formData.guilds.map(guild => guild.value),
        descripcion: formData.description
      };

      await editPredeterminedBudget(editingBudgetId, budgetData);

      // Refrescar la lista de presupuestos
      const budgetsData = await getDefaultBudgets();
      setPresetBudgets(budgetsData);

      // Limpiar el formulario y salir del modo edición
      setFormData({
        name: "",
        guilds: [],
        description: "",
      });
      setIsEditMode(false);
      setEditingBudgetId(null);
    } catch (error) {
      console.error("Error al editar presupuesto predeterminado:", error);
    }
  };

  const handleBudgetRowClick = async (budget) => {
    try {
      const budgetDetails = await getPredeterminedBudgetById(budget.id);

      // Convertir los gremios a formato de opciones seleccionadas
      const selectedGuilds = budgetDetails.gremios?.map(gremio => ({
        value: gremio.id,
        label: gremio.nombre
      })) || [];

      // Llenar el formulario con los datos del presupuesto
      setFormData({
        name: budgetDetails.nombre || "",
        guilds: selectedGuilds,
        description: budgetDetails.descripcion || "",
      });

      setIsEditMode(true);
      setEditingBudgetId(budget.id);
      
      // Hacer scroll al inicio del modal y focus en el primer input
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = 0;
        }
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error("Error al cargar los detalles del presupuesto:", error);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      name: "",
      guilds: [],
      description: "",
    });
    setIsEditMode(false);
    setEditingBudgetId(null);
  };

  const handleSearchPresetBudgets = async (value) => {
    setSearchTerm(value);
    
    if (!value || value.trim() === "") {
      // Si no hay término de búsqueda, cargar todos los presupuestos predefinidos
      try {
        const budgetsData = await getDefaultBudgets();
        setPresetBudgets(budgetsData);
      } catch (error) {
        console.error("Error fetching preset budgets:", error);
      }
      return;
    }

    // Buscar presupuestos predefinidos por nombre
    try {
      const searchResults = await searchPredeterminedBudgetsByName(value);
      setPresetBudgets(searchResults);
    } catch (error) {
      console.error("Error searching preset budgets:", error);
    }
  };

  const headers = [
    { label: "Nombre", key: "nombre" },
    { label: "Gremios", key: "gremios" },
    { label: "Descripción", key: "descripcion" },
  ];

  // Transformar los datos para evitar errores de renderizado
  const presetBudgetsForTable = presetBudgets.map(budget => ({
    id: budget.id,
    nombre: budget.nombre,
    gremios: Array.isArray(budget.gremios) 
      ? budget.gremios.map(g => g.nombre).join(", ")
      : "",
    descripcion: (
      <div className={styles.descriptionCell} title={budget.descripcion || ""}>
        {budget.descripcion || ""}
      </div>
    )
  }));

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Presupuesto predeterminado</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons
              name="close"
              width={24}
              height={24}
              color="black"
            />
          </button>
        </div>

        <div className={styles.formSection}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              width: "100%",
            }}
          >
            <Input
              ref={firstInputRef}
              label="Nombre"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Dropdown
              label="Gremios"
              options={guildOptions}
              onSelect={handleDropdownSelect}
              selected={formData.guilds}
              mainStyle={{ flex: 1, gap: "6px" }}
              style={{ width: "100%" }}
              multiSelect={true}
            />
          </div>
          
            <span className={styles.textareaLabel}>Descripción</span>
            <textarea
              name="description"
              placeholder="Descripción del presupuesto"
              value={formData.description}
              onChange={handleInputChange}
              className={styles.descriptionTextarea}
              style={{
                width: "100%",
                height: "200px",
                paddingTop: "12px",
                paddingBottom: "12px",
                paddingLeft: "12px",
                paddingRight: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontFamily: "var(--font-roboto)",
                fontSize: "14px",
                color: "black",
                resize: "none",
                outline: "none",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                overflow: "auto",
              }}
              spellCheck="true"
            />
         
        </div>

        <div style={{ marginBottom: "16px", display: "flex", gap: "16px" }}>
          {isEditMode ? (
            <>
              <Button type="button" variant="primary" onClick={handleEditPresetBudget}>
                EDITAR PRESUPUESTO
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancelEdit}>
                CANCELAR
              </Button>
            </>
          ) : (
            <Button type="button" variant="primary" onClick={handleAddPresetBudget}>
              AÑADIR PRESUPUESTO
            </Button>
          )}
        </div>

        <div className={styles.tableSection}>
          <Input
            label="Buscar presupuesto predefinido"
            placeholder="Buscar por nombre..."
            value={searchTerm}
            onChange={(e) => handleSearchPresetBudgets(e.target.value)}
            style={{ marginBottom: "16px" }}
          />
          <Table 
            headers={headers} 
            data={presetBudgetsForTable}
            onPress={handleBudgetRowClick}
          />
        </div>
      </div>
    </div>
  );
};

export default PresetBudgetModal;
